import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function HovedrengoeringPage() {
  return (
    <>
      <SEO
        title="Hovedrengøring"
        description="Grundig hovedrengøring for private og erhverv i Havdrup og omegn. Dybderens af køkken, bad, paneler og svært tilgængelige kroge."
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#0d3836]/10 to-white py-12 sm:py-16 lg:py-20 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 flex items-center gap-2 text-xs text-slate-500" aria-label="Brødkrummer">
            <Link to="/" className="hover:text-slate-800 transition-colors">Forside</Link>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span>Ydelser</span>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span className="font-semibold text-slate-900">Hovedrengøring</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0d3836]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#0d3836]">
                <i className="fa-regular fa-sparkles text-[#0d3836]" aria-hidden="true" />
                Hovedrengøring
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
                Dybdegående hovedrengøring med fokus på detaljen
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                Når boligen eller virksomheden trænger til en ekstra grundig gennemgang, tilbyder vi hovedrengøring med fokus på de områder, der normalt ikke bliver taget i den almindelige rengøring.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#0d3836] px-6 py-3.5 text-sm font-semibold text-white shadow-xs hover:bg-[#154e4a] transition-colors text-center"
                >
                  <span>Få et tilbud på hovedrengøring</span>
                  <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="tel:31341632"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors text-center"
                >
                  <i className="fa-regular fa-phone text-[#0d3836]" aria-hidden="true" />
                  <span className="no-break">31 34 16 32</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl bg-slate-100 shadow-md border border-slate-200/80 aspect-4/3">
                <img
                  src={imageUrl('/_assets/shutterstock-2410613279.jpg', 'width=800,quality=85,format=auto')}
                  alt="Dybdegående rengøring og aftørring af køkkenbord og overflader"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detaljeret gennemgang */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-display mb-4">
                  Vi kommer helt i bund og fjerner indgroet snavs
                </h2>
                <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
                  <p>
                    I en travl hverdag er det helt naturligt, at visse opgaver bliver udskudt: rengøring bag komfuret, afkalkning af fuger, aftørring af dørkarme eller rengøring af køkkenskabe.
                  </p>
                  <p>
                    En hovedrengøring fra Havdrup Rengøring giver en forfriskende nulstilling. Vi arbejder metodisk og grundigt igennem alle rum med de rette midler og udstyr, så du kan nyde et hjem eller en virksomhed, der er ren helt i dybden.
                  </p>
                </div>
              </div>

              {/* Tjekliste for hovedrengøring */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-4 flex items-center gap-2">
                  <i className="fa-regular fa-list-check text-[#0d3836]" aria-hidden="true" />
                  Typiske fokuspunkter ved hovedrengøring
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Dybdegående rens af ovn, riste, bageplader og emhætte</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Omfattende afkalkning af brusekabine, fliser og armaturer</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Aftørring af alle døre, karme, fodlister og høje paneler</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Aftørring bag og under flytbare møbler og hårde hvidevarer</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Udvendig og eventuel indvendig aftørring af skabslåger</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836] mt-1 shrink-0" aria-hidden="true" />
                    <span>Grundig gulvbehandling og rengøring af radiatorer</span>
                  </div>
                </div>
              </div>

              {/* Hvad koster hovedrengøring */}
              <div className="rounded-2xl border border-[#0d3836]/15 bg-[#0d3836]/5 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-2">
                  Hvad koster en hovedrengøring?
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Prisen afhænger af boligens eller erhvervslokalernes areal samt hvilke særlige opgaver der ønskes udført (f.eks. indvendig skabsrengøring eller ovnrens). Kontakt os, så giver vi et klart og uforpligtende prisoverslag.
                </p>
                <div className="mt-4">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3836] hover:text-[#154e4a] transition-colors"
                  >
                    <span>Få et uforpligtende tilbud på hovedrengøring</span>
                    <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xs space-y-6">
                <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display">
                  Bestil eller hør mere
                </h3>
                <p className="text-sm text-slate-600">
                  Vi gennemgår gerne dine ønsker forud for opgaven, så vi rammer lige netop det niveau, du har brug for.
                </p>

                <div className="space-y-4 pt-2">
                  <a
                    href="tel:31341632"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 hover:border-[#0d3836]/30 hover:bg-[#0d3836]/5 transition-colors group cursor-pointer"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] group-hover:bg-[#0d3836] group-hover:text-white transition-colors">
                      <i className="fa-regular fa-phone text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Ring direkte</p>
                      <p className="text-base font-bold text-slate-900 no-break">31 34 16 32</p>
                    </div>
                  </a>

                  <a
                    href="mailto:havdruprengoring@gmail.com"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 hover:border-[#0d3836]/30 hover:bg-[#0d3836]/5 transition-colors group cursor-pointer"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] group-hover:bg-[#0d3836] group-hover:text-white transition-colors">
                      <i className="fa-regular fa-envelope text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">E-mail</p>
                      <p className="text-sm font-semibold text-slate-900 no-break">havdruprengoring@gmail.com</p>
                    </div>
                  </a>
                </div>

                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100 text-xs text-slate-600 space-y-2">
                  <div className="flex items-center gap-2">
                    <i className="fa-regular fa-sparkles text-[#0d3836]" aria-hidden="true" />
                    <span>Fokus på de overflader der sjældent nås</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-regular fa-hand-holding-heart text-[#0d3836]" aria-hidden="true" />
                    <span>Skånsomme og effektive kvalitetsprodukter</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
